import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import type { DeviceHealthMessage } from 'lib/medsi/device-health';

interface DeviceHealthAlertDialogProps {
  open: boolean;
  messages: DeviceHealthMessage[];
  onDismiss: () => void;
  /** When set, shows a Rescan action (quality abort path). */
  onRescan?: () => void;
  /** Keeps the current scan running and suppresses abort_recommended until the next scan. */
  onContinue?: () => void;
}

export default function DeviceHealthAlertDialog({
  open,
  messages,
  onDismiss,
  onRescan,
  onContinue,
}: DeviceHealthAlertDialogProps) {
  const { t } = useTranslation();
  const primary = messages[0];
  const extras = messages.slice(1);

  return (
    <Dialog open={open} onOpenChange={next => !next && onDismiss()}>
      <DialogContent
        className='max-w-md px-4 py-6 sm:p-8'
        onOpenAutoFocus={e => e.preventDefault()}
        onInteractOutside={e => e.preventDefault()}
        hideClose
      >
        <DialogHeader>
          <DialogTitle className='text-center sm:text-lg'>
            {primary?.header ?? t('error')}
          </DialogTitle>
          <DialogDescription className='text-center text-muted-foreground'>
            {primary?.description ?? t('medsi_quality_abort_intro')}
          </DialogDescription>
        </DialogHeader>

        {extras.length > 0 ? (
          <div className='max-h-36 space-y-2 overflow-y-auto'>
            {extras.map((msg, idx) => (
              <div
                key={`${msg.header}-${idx}`}
                className='rounded-xl border border-slate-100 bg-slate-50 px-3.5 py-3'
              >
                <h3 className='text-sm font-semibold text-slate-900'>
                  {msg.header}
                </h3>
                <p className='text-xs leading-snug text-slate-500'>
                  {msg.description}
                </p>
              </div>
            ))}
          </div>
        ) : null}

        <div className='flex flex-wrap justify-center gap-3 pt-2'>
          {onContinue ? (
            <Button onClick={onContinue} className='min-w-24 h-9 text-sm'>
              {t('continue')}
            </Button>
          ) : null}
          {onRescan ? (
            <Button
              onClick={onRescan}
              variant={onContinue ? 'outline' : 'default'}
              className='min-w-24 h-9 text-sm'
            >
              {t('rescan')}
            </Button>
          ) : null}
          <Button
            onClick={onDismiss}
            variant={onRescan || onContinue ? 'outline' : 'default'}
            className='min-w-24 h-9 text-sm'
          >
            {t('ok')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
